import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TableRow, TableCell } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { TrendingUp, TrendingDown, MoreHorizontal, Edit, Trash2 } from 'lucide-react';
import SimpleTooltip from '@/components/SimpleTooltip';
import { INVESTMENT_TYPE_LABELS, TYPE_COLORS, formatTableCurrency, formatTableDate } from './investmentTableConstants';

const getRateDisplay = (investment) => {
  if (investment.interest_rate != null) return `${investment.interest_rate}%`;
  if (investment.discount_rate != null) return `${investment.discount_rate}%`;
  return null;
};

const InvestmentDetailsRow = ({ investment, isReadOnly, onEdit, onDelete }) => {
  const profitLoss = (investment.current_value || investment.amount) - investment.amount;
  const returnPct = ((profitLoss / investment.amount) * 100).toFixed(2);
  const isProfit = profitLoss >= 0;
  const rateDisplay = getRateDisplay(investment);
  const institution = investment.institution || investment.fund_manager || investment.etf_fund_manager || investment.issuer_name;

  return (
    <TableRow>
      <TableCell className="font-medium max-w-[180px] truncate">
        <span data-sensitive="asset-name">{investment.name || '-'}</span>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={`text-xs ${TYPE_COLORS[investment.type]}`}>
          {INVESTMENT_TYPE_LABELS[investment.type] || investment.type}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-slate-600 max-w-[140px] truncate">
        <span data-sensitive="identifier">{institution || <span className="text-slate-300">-</span>}</span>
      </TableCell>
      <TableCell className="text-sm text-slate-600">
        {investment.purchase_date
          ? new Date(investment.purchase_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
          : <span className="text-amber-500 text-xs">-</span>}
      </TableCell>
      <TableCell className="text-sm text-slate-600">
        <span data-sensitive="rate">{rateDisplay || <span className="text-slate-300">-</span>}</span>
      </TableCell>
      <TableCell className="text-sm text-slate-600">
        {investment.maturity_date
          ? new Date(investment.maturity_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
          : <span className="text-slate-300">-</span>}
      </TableCell>
      <TableCell className="text-right">
        <span data-sensitive="monetary">{formatTableCurrency(investment.amount, investment.currency)}</span>
      </TableCell>
      <TableCell className="text-right">
        <span data-sensitive="monetary">{formatTableCurrency(investment.current_value || investment.amount, investment.currency)}</span>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end space-x-1">
          {isProfit ? <TrendingUp className="w-4 h-4 text-green-600" /> : <TrendingDown className="w-4 h-4 text-red-600" />}
          <span data-sensitive="rate" className={isProfit ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
            {isProfit ? '+' : ''}{returnPct}%
          </span>
        </div>
      </TableCell>
      <TableCell className="text-slate-500">{formatTableDate(investment.created_at)}</TableCell>
      <TableCell>
        {!isReadOnly && (
          <DropdownMenu>
            <SimpleTooltip content="Edit or delete">
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="More actions for this investment" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
            </SimpleTooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(investment)}><Edit className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(investment.investment_id)} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
};

export default InvestmentDetailsRow;
